import { useEffect, useState } from 'react';
import { useRole } from '@/context/RoleContext';
import { toggleGroup } from '@/utils/roles';
// The catalog is the screen's own permission list (labels, risk levels); roles come from the API.
import { permissionCatalog } from '@/mocks/roles';
import { api } from '@/utils/api';
import type { Permission, RoleRecord } from '@/types/portal';
import RoleList from './components/RoleList';
import RoleSummaryHeader from './components/RoleSummaryHeader';
import PermissionMatrix, { type PermissionLinks } from './components/PermissionMatrix';
import NewRoleModal from './components/NewRoleModal';

export default function Roles() {
  const { can } = useRole();
  const canManage = can('roles.manage');

  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [links, setLinks] = useState<PermissionLinks>({});
  const [selectedId, setSelectedId] = useState('');
  const [draftPermissions, setDraftPermissions] = useState<Permission[]>([]);
  const [draftCeiling, setDraftCeiling] = useState('Not set');
  const [dirty, setDirty] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api<RoleRecord[]>('/roles'), api<PermissionLinks>('/roles/permission-links')])
      .then(([loaded, permissionLinks]) => {
        setRoles(loaded);
        setLinks(permissionLinks);
        const first = loaded.at(0);
        if (first) {
          setSelectedId(first.id);
          setDraftPermissions(first.permissions);
          setDraftCeiling(first.approvalsCeiling);
        }
      })
      .catch((err: Error) => setError(err.message));
  }, []);

  const selected = roles.find((role) => role.id === selectedId) ?? roles.at(0);
  // Laravel's Admin role passes every permission check, so its switches don't apply.
  const isAdminRole = selected?.id === 'admin';

  /** A permission plus every permission saved with it in production. */
  const withSiblings = (keys: Permission[]) =>
    Array.from(new Set(keys.flatMap((key) => (links[key]?.together ?? [key]) as Permission[])));

  const selectRole = (id: string) => {
    const role = roles.find((item) => item.id === id);
    if (!role) return;
    setSelectedId(id);
    setDraftPermissions(role.permissions);
    setDraftCeiling(role.approvalsCeiling);
    setDirty(false);
  };

  // Production stores coarser permissions, so a permission switches together with its siblings.
  const toggle = (key: Permission) => {
    setDraftPermissions((current) => toggleGroup(current, withSiblings([key]), !current.includes(key)));
    setDirty(true);
  };

  const onToggleGroup = (keys: Permission[], on: boolean) => {
    setDraftPermissions((current) => toggleGroup(current, withSiblings(keys), on));
    setDirty(true);
  };

  const changeCeiling = (value: string) => {
    setDraftCeiling(value);
    setDirty(true);
  };

  // RoleController::update. Approval ceilings aren't stored in production.
  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const saved = await api<RoleRecord[]>(`/roles/${selected.id}`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: draftPermissions }),
      });
      setRoles(saved);
      const updated = saved.find((role) => role.id === selected.id);
      if (updated) {
        setDraftPermissions(updated.permissions);
        setDraftCeiling(updated.approvalsCeiling);
      }
      setDirty(false);
      setNotice(
        draftCeiling !== selected.approvalsCeiling
          ? `Permissions saved for ${selected.name}. The approval ceiling isn't stored by the backend yet.`
          : `Permissions saved for ${selected.name}.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the role.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    if (!selected) return;
    setDraftPermissions(selected.permissions);
    setDraftCeiling(selected.approvalsCeiling);
    setDirty(false);
  };

  // RoleController::store. Laravel roles have no description, so it isn't saved.
  const createRole = async (name: string, _description: string, cloneFromId: string) => {
    const source = roles.find((role) => role.id === cloneFromId);
    setError('');
    try {
      const result = await api<{ roles: RoleRecord[]; created: string }>('/roles', {
        method: 'POST',
        body: JSON.stringify({ name, permissions: source ? source.permissions : [] }),
      });
      setRoles(result.roles);
      const role = result.roles.find((item) => item.id === result.created);
      setSelectedId(result.created);
      setDraftPermissions(role?.permissions ?? []);
      setDraftCeiling(role?.approvalsCeiling ?? 'Not set');
      setDirty(false);
      setNewOpen(false);
      setNotice(`${name} created. Assign permissions, then save.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the role.');
      setNewOpen(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <p className="text-xs font-label font-medium uppercase tracking-wider text-foreground-400">
            Settings
          </p>
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Roles &amp; Permissions
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            What each user type can do, and the approval ceiling they carry.
          </p>
        </div>
      </div>

      {notice ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-accent-700 hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="mt-5 flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800"
        >
          <span className="inline-flex items-center gap-2">
            <i className="ri-error-warning-line" />
            {error}
          </span>
          <button
            type="button"
            onClick={() => setError('')}
            className="cursor-pointer text-primary-700 hover:text-primary-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {!canManage ? (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
          <i className="ri-lock-line" />
          Your current role can view roles but not change them. Switch to Administrator to edit
          permissions.
        </div>
      ) : null}

      {!selected ? (
        <div className="mt-6 flex items-center justify-center rounded-lg border border-background-200 bg-background-50 py-16 text-sm text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin" />
          Loading roles…
        </div>
      ) : (
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
        <RoleList
          roles={roles}
          selectedId={selectedId}
          catalog={permissionCatalog}
          canManage={canManage}
          onSelect={selectRole}
          onNew={() => setNewOpen(true)}
        />

        <div className="space-y-4">
          <RoleSummaryHeader
            role={selected}
            permissions={draftPermissions}
            catalog={permissionCatalog}
            ceiling={draftCeiling}
            onCeiling={changeCeiling}
            canManage={canManage}
            dirty={dirty && !saving}
            onSave={() => void save()}
            onCancel={cancel}
          />

          {isAdminRole ? (
            <p className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
              <i className="ri-shield-star-line" />
              Admins always have every permission, so this role can't be narrowed.
            </p>
          ) : null}

          <PermissionMatrix
            catalog={permissionCatalog}
            permissions={draftPermissions}
            onToggle={toggle}
            onToggleGroup={onToggleGroup}
            disabled={!canManage || isAdminRole}
            links={links}
          />
        </div>
      </div>
      )}

      <NewRoleModal
        open={newOpen}
        roles={roles}
        onClose={() => setNewOpen(false)}
        onCreate={(name, description, cloneFromId) => void createRole(name, description, cloneFromId)}
      />
    </div>
  );
}